// La intro del agente de Videos cortos, después de elegir los productos
// (ADR-2026-10-30-videos-cortos-con-el-agente). La misma forma que `IntroDelCatalogo` —las
// miniaturas de lo elegido, Cumbre y su burbuja— pero empujando a lo automático: la burbuja
// AFIRMA lo que el agente va a hacer y, en lugar de la caja, hay dos botones: "Adelante" (el
// CTA) y "Hacer aclaraciones antes", que recién ahí despliega la caja del Catálogo (dictado,
// adjuntos, planillas). Con la caja abierta, Adelante y el enviar de la caja hacen lo mismo.
import { ArrowLeft, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import { useState } from 'react';
import CachedImg from '../../../../components/CachedImg';
import { MascotaConSaludo } from '../../../../chat-atlas/ui/MascotaAgente';
import { useAperturaAnimada } from '../../../../hooks/useAperturaAnimada';
import { useDebajoDeMd } from '../../../../hooks/useEsMobile';
import type { ChatAttachment } from '../../agentAttachments';
import type { MockProductV2 } from '../../mockData';
import { CajaDeCatalogo } from '../catalogoAgente/CajaDeCatalogo';
import { estiloDelMarcoTelefono, MARCO_TELEFONO } from '../catalogoAgente/marcoDelTelefono';
import { useTiraDeSidebar } from '../catalogoAgente/useTiraDeSidebar';
import { pedidoConAclaraciones } from './plan';

/** Cuántas miniaturas se muestran; el resto va como "+N". */
const MINIATURAS = 5;

export const burbujaDeLaIntro = (cantidad: number): string =>
  `Voy a planear un video para cada uno de tus ${cantidad} producto${cantidad === 1 ? '' : 's'}: miro sus fotos, escribo el guion y la voz en off, y elijo la voz que mejor le queda a cada uno. Después los revisás y los creás cuando quieras.`;

export interface IntroDeVideosCortosProps {
  productos: MockProductV2[];
  enviando: boolean;
  error: string | null;
  onEnviar: (texto: string, adjuntos: ChatAttachment[]) => void;
  onVolver: () => void;
}

export const IntroDeVideosCortos: React.FC<IntroDeVideosCortosProps> = ({ productos, enviando, error, onEnviar, onVolver }) => {
  const corrimiento = useTiraDeSidebar(true);
  const esTelefono = useDebajoDeMd();
  const [aclarando, setAclarando] = useState(false);
  const { montado, visible } = useAperturaAnimada(aclarando, 150);
  const [valor, setValor] = useState('');
  const [adjuntos, setAdjuntos] = useState<ChatAttachment[]>([]);
  const conFoto = productos.filter((p) => p.imageUrl);
  const vistos = conFoto.slice(0, MINIATURAS);
  const resto = productos.length - vistos.length;
  const adelante = () => onEnviar(pedidoConAclaraciones(aclarando ? valor : ''), aclarando ? adjuntos : []);

  return (
    <div className={`flex h-full flex-col overflow-y-auto ${MARCO_TELEFONO}`} style={estiloDelMarcoTelefono(esTelefono)}>
      <div className="hidden shrink-0 pr-6 pt-5 md:block" style={{ paddingLeft: corrimiento + 24 }}>
        <button
          type="button"
          onClick={onVolver}
          disabled={enviando}
          className="inline-flex items-center gap-1.5 rounded-full py-1.5 px-3 text-[13.5px] font-semibold text-[#3C3C43]/70 transition-colors hover:bg-black/[0.04] hover:text-brand-black disabled:opacity-40"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
          Cambiar productos
        </button>
      </div>
      <div className="mx-auto flex w-full max-w-[760px] flex-1 flex-col items-center justify-center gap-6 px-4 pb-8 sm:gap-8 sm:px-6 sm:pb-10">
        <div className="flex items-center justify-center -space-x-3" aria-label={`${productos.length} producto${productos.length === 1 ? '' : 's'} elegido${productos.length === 1 ? '' : 's'}`}>
          {vistos.map((p, i) => (
            <span
              key={p.id}
              className="animate-fade-in block h-[76px] w-[76px] overflow-hidden rounded-2xl bg-white shadow-md ring-[3px] ring-white"
              style={{
                transform: `rotate(${((i / Math.max(1, vistos.length - 1)) * 2 - 1) * 8}deg)`,
                animationDelay: `${i * 60}ms`,
                zIndex: vistos.length - Math.abs(i - (vistos.length - 1) / 2),
              }}
            >
              <CachedImg src={p.imageUrl as string} alt="" className="h-full w-full object-cover" />
            </span>
          ))}
          {resto > 0 && (
            <span className="relative grid h-[52px] min-w-[52px] place-items-center rounded-2xl bg-[rgb(var(--brand-primary-rgb)_/_0.095)] px-3 text-[14px] font-semibold text-brand-primary ring-[3px] ring-white">
              +{resto}
            </span>
          )}
        </div>

        {/* Cumbre AFIRMA lo que va a hacer: la forma de un mensaje del agente con su avatar. */}
        <div className="flex w-full items-end justify-center gap-3 sm:gap-5">
          <MascotaConSaludo size={120} burbuja="arriba" saludo={false} className="h-auto w-[72px] shrink-0 sm:w-[96px]" />
          <p className="min-w-0 rounded-[18px] rounded-tl-[6px] bg-[#F2F2F7] px-4 py-3 text-left text-[16px] font-medium leading-snug text-brand-black sm:px-5 sm:py-4 sm:text-[18px]">{burbujaDeLaIntro(productos.length)}</p>
        </div>

        <div className="flex w-full flex-col items-center gap-3">
          {montado && (
            <div
              className={`w-full transition-[opacity,transform,filter] duration-300 ease-out motion-safe:transform ${
                visible ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 motion-safe:translate-y-3 motion-safe:blur-[4px]'
              }`}
            >
              <CajaDeCatalogo
                valor={valor}
                onCambiarValor={setValor}
                adjuntos={adjuntos}
                onAgregarAdjunto={(a) => setAdjuntos((prev) => [...prev, a])}
                onQuitarAdjunto={(id) => setAdjuntos((prev) => prev.filter((a) => a.id !== id))}
                onEnviar={(t) => onEnviar(pedidoConAclaraciones(t), adjuntos)}
                placeholder="Ej: sin voz en off, un tono más formal, que se vea en una cocina…"
                bloqueado={enviando}
                motivoBloqueo="Abriendo el lote…"
                alto="amplio"
                autoFoco
              />
            </div>
          )}
          {/* Los dos botones en una fila: la pill blanca de aclarar a la izquierda del CTA. Con
              la caja abierta queda sólo Adelante. */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {!aclarando && (
              <button
                type="button"
                onClick={() => setAclarando(true)}
                disabled={enviando}
                className="inline-flex items-center rounded-full bg-white py-3 px-6 text-[16px] font-semibold text-brand-black ring-1 ring-black/[0.08] shadow-[0_2px_8px_rgb(var(--brand-black-rgb)_/_0.06)] transition-[background-color,transform] hover:bg-neutral-50 active:scale-[0.96] disabled:opacity-40 disabled:active:scale-100"
              >
                Hacer aclaraciones antes
              </button>
            )}
            <button
              type="button"
              onClick={adelante}
              disabled={enviando}
              className="inline-flex items-center gap-2 rounded-full bg-brand-primary py-3 px-6 text-[16px] font-semibold text-white shadow-[0_8px_24px_-10px_rgb(var(--brand-primary-rgb)_/_0.6)] transition-[background-color,transform] hover:bg-brand-primary-hover active:scale-[0.96] disabled:opacity-60 disabled:active:scale-100"
            >
              {enviando ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Sparkles className="h-5 w-5" strokeWidth={2} aria-hidden="true" />}
              Adelante
              {!enviando && <ArrowRight className="h-[18px] w-[18px]" strokeWidth={2.25} aria-hidden="true" />}
            </button>
          </div>
          {enviando && (
            <p className="flex items-center justify-center gap-2 text-[13px] text-[#3C3C43]/60">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Abriendo el lote…
            </p>
          )}
          {error && <p className="text-center text-[13px] text-[#B42318]">{error}</p>}
        </div>
      </div>
    </div>
  );
};
